/**
 * ============================================================================
 * 문의하기 페이지
 * ============================================================================
 * 
 * 문의 게시판 페이지 컴포넌트
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 기능:
 * - 문의 목록 조회 및 표시
 * - 문의 상세 보기 (확장/축소)
 * - 문의 작성 모달
 * - 검색 및 필터링
 * - 페이지네이션
 * 
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ALL_ROUTES } from '../../../core/router/routes';
import SubLayout from '../../components/layout/SubLayout';
import { useFrontAuthStore } from '../../auth/store/authStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { attachmentService } from '../../services/attachmentService';
import {
  getTickets,
  getTicket,
  getTicketMessages,
  deleteTicket,
} from '../../services/inquiryService';
import {
  Ticket,
  TicketListItem,
  TicketCategory,
  TicketStatus,
  TicketCreateRequest,
  TicketMessage,
  TicketAttachment,
} from '../../types/inquiry';

const Container = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.lg};
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const SearchSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: var(--bg-secondary);
  color: ${props => props.theme.colors.book.color2b};
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  height: 44px;
  outline: none;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.book.color2b};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  max-width: 400px;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: var(--bg-secondary);
  color: ${props => props.theme.colors.book.color2b};
  font-size: ${props => props.theme.fontSize.base};
  height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color, #3b82f6);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.book.color9b};
  }
`;

const SearchButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.de};
  background-color: var(--bg-secondary);
  width: 44px;
  height: 44px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: var(--bg-secondary, #f5f5f5);
  height: 60px;
  width: 100%;
`;

const TableHeaderRow = styled.tr`
  width: 100%;
  height: 60px;
  border-bottom: 1px solid ${props => props.theme.colors.border.de};
  background: var(--bg-secondary);
`;

const TableHeaderCell = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color2b};
  font-size: ${props => props.theme.fontSize.base};
  text-align:center;
  
  &:first-child {
    width: 120px;
  }
  &:nth-child(2) {
    width: calc(100% - 320px);
  }
  &:nth-child(3) {
    width: 120px;
  }
  &:last-child {
    width: 80px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $isExpanded?: boolean }>`
  border-bottom: 1px solid ${props => props.$isExpanded ? 'transparent' : props.theme.colors.border.de};
  cursor: pointer;
  transition: background-color 0.2s;
  background: var(--bg-secondary);
  
  &:hover {
    background: var(--bg-secondary, #f5f5f5);
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  text-align:center;
  height: 60px;
  &:nth-child(2) {
    text-align: left;
  }
`;

const CategoryBadge = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
`;

const StatusBadge = styled.span<{ $hasAnswer?: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => 
    props.$hasAnswer 
      ? '#eff6ff' 
      : '#f3f4f6'};
  color: ${props => 
    props.$hasAnswer 
      ? '#3b82f6' 
      : '#6b7280'};
`;

const ExpandIcon = styled.span<{ $isExpanded?: boolean }>`
  display: inline-block;
  transition: transform 0.2s;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const ExpandedContent = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing['5xl']};
  background: var(--bg-secondary);
`;

const ContentSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const InquirySection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const AnswerCard = styled.div`
  background: var(--bg-secondary);
  border-top: 1px dashed ${props => props.theme.colors.border.de};
  padding: ${props => props.theme.spacing['3xl']} 0;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #333);
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: left;
`;

const ContentText = styled.div`
  text-align: left;
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.book.color6b};
  font-weight: ${props => props.theme.fontWeight.normal};
  line-height: 1.8;
  white-space: pre-wrap;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const AttachmentPreview = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

const AttachmentImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  margin-bottom: ${props => props.theme.spacing.sm};
  cursor: pointer;
  border: 1px dashed ${props => props.theme.colors.border.de};
`;

const AttachmentListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const AttachmentListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm};
  background: var(--bg-secondary, #f5f5f5);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 14px;
`;

const AttachmentLink = styled.a`
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentInfo = styled.div`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: 12px;
  color: var(--text-secondary, #666);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.md};
`;

const MessageDate = styled.div`
  text-align: left;
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.book.color7b};
  margin-top: ${props => props.theme.spacing.sm};
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const PageButton = styled.button<{ $isActive?: boolean }>`
  padding: ${props => props.theme.spacing.sm};
  color: ${props => 
    props.$isActive 
      ? props.theme.colors.book.color2b 
      : props.theme.colors.book.color6b};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InquiryBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  margin-top: ${props => props.theme.spacing.xl};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.md};
`;

const FileInput = styled.input`
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 14px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background: var(--bg-secondary, #f5f5f5);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 12px;
`;

const MENU_ITEMS = [
  {
    key: 'profile',
    label: '프로필 수정',
    icon: '/images/book/icon_user.svg',
    children: [
      { key: 'settings-general', label: '기본 정보 설정', path: '/profile' },
      { key: 'settings-school', label: '학적 정보 설정', path: '/profile' },
    ],
  },
  { key: 'inquiry', label: '문의하기', icon: '/images/book/icon_inquiry.svg', path: '/inquiry', active: true },
  { key: 'logout', label: '로그아웃' },
];

const InquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useFrontAuthStore();
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [ticketDetail, setTicketDetail] = useState<Ticket | null>(null);
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // 검색 및 필터 상태
  const [status, setStatus] = useState<'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | ''>('');
  const [category, setCategory] = useState<TicketCategory | ''>('');
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSuccessModalOpen, setIsDeleteSuccessModalOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<number | null>(null);

  const limit = 20;

  // 티켓 목록 조회
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * limit;
      const response = await getTickets({
        status: status || undefined,
        category: category || undefined,
        q: searchKeyword || undefined,
        offset,
        limit,
        include_deleted: false,
      });
      
      setTickets(response.items);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (error) {
      // console.error('문의 목록 조회 실패:', error);
      alert('문의 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [status, category, searchKeyword, currentPage, limit]);

  // 티켓 상세 조회
  const fetchTicketDetail = useCallback(async (ticketNo: number) => {
    try {
      const detail = await getTicket(ticketNo);
      setTicketDetail(detail);
    } catch (error) {
      // console.error('문의 상세 조회 실패:', error);
      alert('문의 상세를 불러오는데 실패했습니다.');
    }
  }, []);

  // 티켓 메시지 목록 조회 (답변 목록)
  const fetchTicketMessages = useCallback(async (ticketNo: number) => {
    try {
      const response = await getTicketMessages(ticketNo, {
        limit: 50,
        include_internal: false,
      });
      setTicketMessages(response.items);
    } catch (error) {
      // console.error('답변 목록 조회 실패:', error);
      // 에러 발생 시에도 사용자에게 알리지 않고 조용히 처리
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // 티켓 항목 클릭 (확장/축소)
  const handleTicketClick = async (ticketUuid: string, ticketNo: number) => {
    if (expandedTicket === ticketUuid) {
      setExpandedTicket(null);
      setTicketDetail(null);
      setTicketMessages([]);
    } else {
      // 먼저 확장 상태를 설정하여 즉시 UI 표시
      setExpandedTicket(ticketUuid);
      setTicketDetail(null);
      setTicketMessages([]);
      
      // 병렬로 API 호출하되, 각각 완료되는 대로 상태 업데이트
      Promise.all([
        fetchTicketDetail(ticketNo),
        fetchTicketMessages(ticketNo),
      ]).catch((error) => {
        // console.error('문의 상세/답변 조회 실패:', error);
      });
    }
  };

  // 검색
  const handleSearch = () => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };


  // 티켓 수정 페이지로 이동
  const handleEdit = () => {
    if (ticketDetail) {
      navigate(`/inquiry/edit/${ticketDetail.ticket_no}`);
    }
  };

  // 티켓 삭제
  const handleDelete = async () => {
    if (!ticketToDelete) return;

    try {
      await deleteTicket(ticketToDelete);
      setIsDeleteModalOpen(false);
      setTicketToDelete(null);
      
      // 삭제된 티켓이 현재 확장된 티켓이면 닫기
      if (ticketDetail && ticketDetail.ticket_no === ticketToDelete) {
        setExpandedTicket(null);
        setTicketDetail(null);
        setTicketMessages([]);
      }
      
      // 목록 새로고침
      await fetchTickets();
      
      // 삭제 성공 모달 표시
      setIsDeleteSuccessModalOpen(true);
    } catch (error) {
      // console.error('문의 삭제 실패:', error);
      alert('문의를 삭제하는데 실패했습니다.');
    }
  };

  // 삭제 모달 열기
  const handleOpenDeleteModal = (ticketNo: number) => {
    setTicketToDelete(ticketNo);
    setIsDeleteModalOpen(true);
  };

  // 카테고리 이름 변환
  const getCategoryName = (cat: TicketCategory): string => {
    const names: Record<TicketCategory, string> = {
      QUESTION: '문의',
      BUG: '버그',
      FEATURE: '기능',
      COMPLAINT: '불만',
      ETC: '기타',
    };
    return names[cat];
  };

  // 상태 이름 변환 (답변 여부에 따라)
  const getStatusName = (ticket: TicketListItem | Ticket, hasAnswer?: boolean): string => {
    // 답변이 있으면 "답변 완료", 없으면 "답변 대기"
    if (hasAnswer !== undefined) {
      return hasAnswer ? '답변 완료' : '답변 대기';
    }
    // hasAnswer가 없으면 latest_message로 판단
    const hasMessage = ticket.latest_message !== null;
    return hasMessage ? '답변 완료' : '답변 대기';
  };

  // 답변 여부 확인
  const hasAnswer = (ticket: TicketListItem | Ticket): boolean => {
    return ticket.latest_message !== null;
  };

  // 날짜 포맷
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  const handleMenuClick = useCallback(async (key: string) => {
    if (key === 'logout') {
      try { await logout(); } catch (e) { /* ignore */ }
    }
  }, [logout]);

  return (
    <SubLayout
      menuItems={MENU_ITEMS}
      activeMenu="inquiry"
      onMenuClick={handleMenuClick}
    >
      <Container>
        <Header>
          <Button variant="primary" size="md" onClick={() => navigate(ALL_ROUTES.INQUIRY_CREATE)}>
            1:1 문의 작성
          </Button>
          <SearchSection>
            <FilterSelect
              value={status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setStatus(e.target.value as 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | '');
                setCurrentPage(1);
              }}
            >
              <option value="">전체 상태</option>
              <option value="OPEN">답변 대기</option>
              <option value="CLOSED">답변 완료</option>
            </FilterSelect>
            <FilterSelect
              value={category}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setCategory(e.target.value as TicketCategory | '');
                setCurrentPage(1);
              }}
            >
              <option value="">전체 분류</option>
              <option value="QUESTION">문의</option>
              <option value="BUG">버그</option>
              <option value="FEATURE">기능</option>
            </FilterSelect>
            <SearchInput
              placeholder="제목, 내용으로 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <SearchButton onClick={handleSearch}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15.4165 15.4165L18.3332 18.3332M5.62485 2.72485C7.28826 1.76495 9.24388 1.44245 11.1274 1.81742C13.011 2.19239 14.694 3.23927 15.8629 4.76303C17.0319 6.28679 17.6071 8.18353 17.4814 10.0999C17.3557 12.0163 16.5377 13.8216 15.1797 15.1797C13.8216 16.5377 12.0163 17.3557 10.0999 17.4814C8.18353 17.6071 6.28679 17.0319 4.76303 15.8629C3.23927 14.694 2.19239 13.011 1.81742 11.1274C1.44245 9.24388 1.76495 7.28826 2.72485 5.62485" stroke="#6B6B6B" strokeWidth="1.25" strokeLinecap="round"/>
              </svg>
            </SearchButton>
          </SearchSection>
        </Header>

        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>분류</TableHeaderCell>
              <TableHeaderCell>제목</TableHeaderCell>
              <TableHeaderCell>작성일</TableHeaderCell>
              <TableHeaderCell>
              </TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                  문의 내역이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => (
                <React.Fragment key={ticket.ticket_uuid}>
                  <TableRow
                    $isExpanded={expandedTicket === ticket.ticket_uuid}
                    onClick={() => handleTicketClick(ticket.ticket_uuid, ticket.ticket_no)}
                  >
                    <TableCell>
                      <CategoryBadge>{getCategoryName(ticket.category)}</CategoryBadge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>{ticket.title}</span>
                        <StatusBadge $hasAnswer={hasAnswer(ticket)}>
                          {getStatusName(ticket)}
                        </StatusBadge>
                        {/* 첨부파일 표시 숨김 처리
                        {ticket.attachments_summary && ticket.attachments_summary.count > 0 && (
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            📎 {ticket.attachments_summary.count}개
                          </span>
                        )}
                        */}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(ticket.created_at)}</TableCell>
                    <TableCell>
                      <ExpandIcon $isExpanded={expandedTicket === ticket.ticket_uuid}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="10" viewBox="0 0 18 10" fill="none">
                          <path d="M16.9238 1.92476L9.42383 9.42476C9.31931 9.52964 9.19512 9.61286 9.05837 9.66964C8.92163 9.72642 8.77502 9.75565 8.62695 9.75565C8.47889 9.75565 8.33228 9.72642 8.19553 9.66964C8.05878 9.61286 7.93459 9.52964 7.83008 9.42476L0.330077 1.92476C0.118732 1.71342 -3.14928e-09 1.42677 0 1.12789C3.14928e-09 0.829003 0.118732 0.542358 0.330077 0.331014C0.541421 0.11967 0.828065 0.000937703 1.12695 0.0009377C1.42584 0.000937697 1.71248 0.11967 1.92383 0.331014L8.62789 7.03508L15.332 0.330077C15.5433 0.118733 15.8299 0 16.1288 0C16.4277 0 16.7144 0.118733 16.9257 0.330077C17.137 0.541421 17.2558 0.828066 17.2558 1.12695C17.2558 1.42584 17.137 1.71248 16.9257 1.92383L16.9238 1.92476Z" fill="#4B4B4B"/>
                        </svg>
                      </ExpandIcon>
                    </TableCell>
                  </TableRow>
                  {expandedTicket === ticket.ticket_uuid && (
                    <TableRow>
                      <TableCell colSpan={4} style={{ padding: 0 }}>
                        <ExpandedContent>
                          {ticketDetail ? (
                            <>
                              <InquirySection>
                                
                                <ContentText>{ticketDetail.content}</ContentText>
                                {/* 첨부파일 미리보기 숨김 처리
                                {ticketDetail.attachments && ticketDetail.attachments.length > 0 && (
                                  <AttachmentPreview>
                                    {ticketDetail.attachments.map((attachment) => {
                                      const isImage = attachment.mime_type?.startsWith('image/');
                                      const fileUrl = attachmentService.getFullFileUrl(attachment.file_uuid);

                                      return (
                                        <div key={attachment.attachment_no} style={{ marginBottom: '16px' }}>
                                          {isImage && (
                                            <AttachmentImage
                                              src={fileUrl}
                                              alt={attachment.original_name}
                                              onClick={() => window.open(fileUrl, '_blank')}
                                            />
                                          )}
                                          <AttachmentListContainer>
                                            <AttachmentListItem>
                                              <div>
                                                <AttachmentLink
                                                  href={fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    window.open(fileUrl, '_blank');
                                                  }}
                                                >
                                                  {attachment.original_name}
                                                </AttachmentLink>
                                                <AttachmentInfo>
                                                  {attachmentService.formatFileSize(attachment.file_size)}
                                                </AttachmentInfo>
                                              </div>
                                            </AttachmentListItem>
                                          </AttachmentListContainer>
                                        </div>
                                      );
                                    })}
                                  </AttachmentPreview>
                                )}
                                */}
                                <ActionButtons onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  >
                                    수정
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      handleOpenDeleteModal(ticketDetail.ticket_no);
                                    }}
                                  >
                                    삭제
                                  </Button>
                                </ActionButtons>
                              </InquirySection>
                              {ticketMessages.length > 0 ? (
                                ticketMessages.map((message) => (
                                  <AnswerCard key={message.message_no}>
                                    <SectionTitle>답변</SectionTitle>
                                    <ContentText>{message.body}</ContentText>
                                    <MessageDate>
                                      {formatDate(message.created_at)}
                                    </MessageDate>
                                  </AnswerCard>
                                ))
                              ) : ticketDetail.latest_message ? (
                                <AnswerCard>
                                  <SectionTitle>답변</SectionTitle>
                                  <ContentText>{ticketDetail.latest_message.body}</ContentText>
                                  <MessageDate>
                                    {formatDate(ticketDetail.latest_message.created_at)}
                                  </MessageDate>
                                </AnswerCard>
                              ) : null}
                            </>
                          ) : (
                            <div style={{ padding: '2rem', textAlign: 'center' }}>로딩 중...</div>
                          )}
                        </ExpandedContent>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
            
        <InquiryBottom>

          {total > 0 && (
            <Pagination>
              <PageButton
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M10.3255 2.95361C10.3754 2.99636 10.4163 3.04853 10.446 3.10712C10.4757 3.16571 10.4936 3.22959 10.4986 3.29509C10.5037 3.36059 10.4957 3.42644 10.4753 3.48888C10.4548 3.55131 10.4223 3.6091 10.3795 3.65895L6.65881 8.00028L10.3795 12.3416C10.4247 12.391 10.4596 12.4491 10.482 12.5123C10.5044 12.5754 10.5139 12.6425 10.5098 12.7094C10.5058 12.7763 10.4883 12.8417 10.4585 12.9017C10.4287 12.9618 10.3871 13.0152 10.3362 13.0588C10.2854 13.1025 10.2262 13.1354 10.1623 13.1557C10.0985 13.1761 10.0312 13.1833 9.96442 13.1771C9.89768 13.1709 9.83287 13.1514 9.77384 13.1196C9.71481 13.0879 9.66277 13.0446 9.62081 12.9923L5.62081 8.32561C5.54316 8.235 5.50049 8.11961 5.50049 8.00028C5.50049 7.88095 5.54316 7.76556 5.62081 7.67495L9.62081 3.00828C9.70713 2.90774 9.82984 2.84557 9.96196 2.83545C10.0941 2.82532 10.2248 2.86806 10.3255 2.95428" fill="#9B9B9B"/>
                </svg>
              </PageButton>
              {totalPages > 0 && Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <PageButton
                      key={page}
                      $isActive={currentPage === page}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                      <span key={page}> / {totalPages}</span>
                    </PageButton>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return <span key={page}>...</span>;
                }
                return null;
              })}
              <PageButton
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M5.67458 2.95302C5.7753 2.86688 5.90609 2.82426 6.03823 2.83451C6.17036 2.84476 6.29302 2.90705 6.37924 3.00769L10.3792 7.67435C10.4569 7.76497 10.4996 7.88036 10.4996 7.99969C10.4996 8.11901 10.4569 8.23441 10.3792 8.32502L6.37924 12.9917C6.29159 13.0874 6.17015 13.1453 6.04059 13.1531C5.91103 13.1609 5.78351 13.118 5.68499 13.0335C5.58647 12.949 5.52467 12.8296 5.51265 12.7003C5.50063 12.5711 5.53933 12.4422 5.62058 12.341L9.34124 7.99969L5.62058 3.65835C5.53428 3.5578 5.49143 3.42711 5.50143 3.29498C5.51143 3.16286 5.57346 3.0401 5.67391 2.95369" fill="#9B9B9B"/>
                </svg>
              </PageButton>
            </Pagination>
          )}
          <Button
              variant="secondary"
              size="lg"
              width={130}                
              onClick={() => navigate(-1)}
          >
            뒤로가기
          </Button>
        </InquiryBottom>

        {/* 문의 삭제 확인 모달 */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setTicketToDelete(null);
          }}
          title="문의 삭제"
        >
          <ModalContent>
            <p>정말로 이 문의를 삭제하시겠습니까?</p>
            <ModalActions>
              <Button variant="secondary" onClick={() => {
                setIsDeleteModalOpen(false);
                setTicketToDelete(null);
              }}>
                취소
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                삭제
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>

        {/* 문의 삭제 성공 모달 */}
        <Modal
          isOpen={isDeleteSuccessModalOpen}
          onClose={() => setIsDeleteSuccessModalOpen(false)}
          title="삭제 완료"
        >
          <ModalContent>
            <p>문의가 삭제되었습니다.</p>
            <ModalActions>
              <Button onClick={() => setIsDeleteSuccessModalOpen(false)}>
                확인
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      </Container>
    </SubLayout>
  );
};

export default InquiryPage;
